/**
 * `/` — everything the customer owns, grouped by product.
 *
 * This screen is the answer to "what did I buy and where is my key", which is
 * the only question most visitors will ever bring here. So it is a list of
 * licenses and nothing else: no counters, no activity feed, no "welcome back".
 *
 * The three states below all get real screens rather than a spinner and a
 * shrug, because two of them are common and one of them is the most important
 * screen in the product:
 *
 *   empty          — a customer who bought through a marketplace, signed in,
 *                    and owns nothing under this address. This is NOT an error
 *                    and must not read like one; it is the moment they need to
 *                    be told about adding a key, and it is the reason `/claim`
 *                    exists.
 *   unreachable    — aloftwx-api is down or its entrypoint has not shipped. The
 *                    customer's licenses are unaffected and the copy says so,
 *                    because "we cannot reach the licensing service" reads like
 *                    "your license is gone" to somebody who just paid.
 *   loaded         — the list.
 */

import { PageHeader } from "@/components/PageHeader";
import { StatusChip } from "@/components/StatusChip";
import { Empty, Marker, Notice, Strip, StripBody, StripHead, Tag } from "@/components/deck";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PortalError, portalApi, useApiQuery } from "@/lib/api";
import type { CustomerLicense } from "@/lib/api";
import { isoDate } from "@/lib/format";
import { ArrowRight, Download, ExternalLink } from "lucide-react";
import { Link } from "react-router";
import { PRODUCT_LINKS, byProduct, editionName } from "../../shared/portal";

export function OverviewRoute() {
  const licenses = useApiQuery([], (signal) => portalApi.listLicenses(signal), {
    // The screen renders its own failure — a toast on top of a full-width
    // notice saying the same thing twice is noise.
    suppressToast: true,
  });

  return (
    <>
      <PageHeader
        code="LIC"
        label="Overview"
        title={
          <>
            Your <Marker draw>licenses</Marker>
          </>
        }
        description="Every product you own, the key for each one, and the machines it is running on."
        actions={
          <Button asChild variant="outline">
            <Link to="/claim">Add a key</Link>
          </Button>
        }
      />

      {licenses.isLoading ? <LoadingPanels /> : null}

      {licenses.error ? <LoadFailed error={licenses.error} onRetry={licenses.refetch} /> : null}

      {licenses.data && licenses.data.licenses.length === 0 ? <NothingYet /> : null}

      {licenses.data && licenses.data.licenses.length > 0 ? (
        <div className="flex flex-col gap-6">
          {byProduct(licenses.data.licenses).map((group) => (
            <ProductPanel
              key={group.product}
              slug={group.product}
              name={group.product_name}
              licenses={group.licenses}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function ProductPanel({
  slug,
  name,
  licenses,
}: {
  slug: string;
  name: string;
  licenses: CustomerLicense[];
}) {
  // An unknown slug renders with no links rather than breaking. A second
  // product has to be able to appear here before `PRODUCT_LINKS` knows it —
  // the API is the one that decides what exists.
  const links = PRODUCT_LINKS[slug];

  return (
    <section aria-labelledby={`product-${slug}`}>
      <Strip bar={false}>
        <StripHead title={name} id={`product-${slug}`}>
          {links?.download ? (
            <Button asChild variant="ghost" size="sm">
              <a href={links.download}>
                <Download aria-hidden="true" />
                Download
              </a>
            </Button>
          ) : null}
          {links?.home ? (
            <Button asChild variant="ghost" size="sm">
              <a href={links.home}>
                <ExternalLink aria-hidden="true" />
                Product page
              </a>
            </Button>
          ) : null}
        </StripHead>

        <div>
          {licenses.map((license) => (
            <LicenseRow key={license.id} license={license} />
          ))}
        </div>
      </Strip>
    </section>
  );
}

/**
 * One license, as a whole row you can click.
 *
 * Not a `Ledger` row: the console's dense table is right for an operator
 * scanning fifty results, and wrong for a customer looking at one or two
 * records where every field needs its label visible. Same forms, more air —
 * which is the departure the portal is allowed to make from the console.
 *
 * `seats_used / seat_limit` is the field a customer actually comes here for
 * after "what is my key", so it is marked when it is at the limit. That is the
 * pen mark meaning what it means everywhere else in the product: somebody needs
 * to look at this.
 */
function LicenseRow({ license }: { license: CustomerLicense }) {
  const atLimit = license.seats_used >= license.seat_limit;

  return (
    <Link
      to={`/licenses/${license.id}`}
      className="group flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-rule px-4 py-4 transition-colors last:border-b-0 hover:bg-marker/10 focus-visible:-outline-offset-2"
    >
      <div className="min-w-0 flex-1 basis-52">
        <div className="t-field">Key</div>
        <div className="t-data mt-1 truncate text-[0.9375rem] text-ink">
          {license.key_prefix}
          <span className="text-ink-3">…</span>
        </div>
      </div>

      <div className="min-w-0">
        <div className="t-field">Edition</div>
        <div className="mt-1 truncate text-[0.9375rem] text-ink">
          {editionName(license.edition)}
        </div>
      </div>

      <div className="min-w-0">
        <div className="t-field">Devices</div>
        <div className="t-data mt-1 text-[0.9375rem] text-ink">
          {atLimit ? (
            <Marker>
              {license.seats_used} / {license.seat_limit}
            </Marker>
          ) : (
            <>
              {license.seats_used} / {license.seat_limit}
            </>
          )}
        </div>
      </div>

      <div className="min-w-0 max-sm:hidden">
        <div className="t-field">Since</div>
        <div className="t-data mt-1 text-[0.9375rem] text-ink">{isoDate(license.created_at)}</div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <StatusChip status={license.status} />
        <ArrowRight
          aria-hidden="true"
          className="size-4 text-ink-3 transition-colors group-hover:text-ink"
        />
      </div>
    </Link>
  );
}

function LoadingPanels() {
  return (
    <Strip bar={false}>
      <StripHead title="Loading" as="h2" />
      <div>
        {[0, 1].map((i) => (
          <div key={i} className="border-b border-rule px-4 py-5 last:border-b-0">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="mt-3 h-3 w-1/3" />
          </div>
        ))}
      </div>
    </Strip>
  );
}

/**
 * The failure that matters, and the sentence that keeps it from being a
 * support ticket.
 *
 * A customer who has just paid and sees "we cannot reach the licensing service"
 * reads it as "my license is gone". The first line of this notice therefore
 * says what is true — nothing has happened to their license — before it says
 * what went wrong.
 */
function LoadFailed({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const unreachable = error instanceof PortalError && error.status === 503;

  return (
    <Notice
      tone="hold"
      label={unreachable ? "Licensing service unreachable" : "Could not load"}
      action={
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      }
    >
      {unreachable ? (
        <>
          Your licenses are fine — this is our side, and nothing about what you own has changed. The
          app on your PC keeps working while this is down. Try again in a few minutes.
        </>
      ) : (
        error.message
      )}
    </Notice>
  );
}

/**
 * Not an error state. A customer who bought from a marketplace lands here on
 * their first ever sign-in, and everything on this screen is written for them.
 */
function NothingYet() {
  return (
    <Strip>
      <StripHead title="No licenses on this address" as="h2" id="empty-head">
        <Tag dot={false}>ACCOUNT READY</Tag>
      </StripHead>
      <StripBody className="pl-5">
        <Empty className="px-0 py-4 text-left">
          <span className="block text-[1rem] text-ink">
            Nothing is registered to this email address yet. That is normal, and it is usually one
            of two things.
          </span>
        </Empty>

        <ul className="mt-2 flex flex-col gap-4">
          <li className="border-l border-rule pl-4">
            <div className="t-field">Bought somewhere other than downwind.studio</div>
            <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-ink-2">
              Marketplaces hand over a key without telling us who bought it. Add your key and it
              becomes part of this account permanently.
            </p>
          </li>
          <li className="border-l border-rule pl-4">
            <div className="t-field">Bought from us, under a different address</div>
            <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-ink-2">
              Sign in with the address on your order confirmation — or add the key from that order
              here, which does the same job and leaves you with one account.
            </p>
          </li>
        </ul>

        <Button asChild size="lg" className="mt-6">
          <Link to="/claim">Add a license key</Link>
        </Button>
      </StripBody>
    </Strip>
  );
}
